import { Box, Flex, LinkBox, LinkOverlay, Text, type BoxProps } from "@chakra-ui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { DeadlineKind, type UpcomingDeadlineDto } from "../../../autogen/bff-api";
import { UnansweredQuestionsBadge } from "../../../common/components/communication/UnansweredQuestionsBadge";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { deadlineTarget, TIMED_DEADLINE_KINDS } from "../deadlineTarget";
import { URGENCY_COLOR, deadlineUrgency } from "../deadlineUrgency";
import { useDeadlineLabel } from "../useDeadlineLabel";
import { LINKED_ROW_FOCUS } from "./PanelTable";

export const DeadlineRow = ({
  deadline,
  children,
  ...rowProps
}: { deadline: UpcomingDeadlineDto } & BoxProps) => {
  const { t, i18n } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const deadlineLabel = useDeadlineLabel();
  const urgency = deadlineUrgency(deadline.dueAt);
  return (
    <LinkBox
      as={Flex}
      position="relative"
      cursor="pointer"
      role="group"
      sx={LINKED_ROW_FOCUS}
      {...rowProps}
    >
      {children}
      <Box minW="0" flex="1">
        <Flex align="baseline" gap="2" wrap="wrap">
          <Text fontSize="sm" color={URGENCY_COLOR[urgency]}>
            {TIMED_DEADLINE_KINDS.has(deadline.kind) ? displayDateWithTime(deadline.dueAt) : displayDate(deadline.dueAt)}
          </Text>
          <Text fontSize="xs" color={urgency === "overdue" ? "smError" : "smMuted"}>
            {urgency === "overdue" && `${t("Past the deadline")} · `}
            {moment(deadline.dueAt).locale(momentLocaleFor(i18n.language)).fromNow()}
          </Text>
        </Flex>
        <Text isTruncated color="smMuted" _groupHover={{ textDecoration: "underline" }}>
          <LinkOverlay as={RouterLink} to={deadlineTarget(deadline)}>
            {deadline.subjectTitle || t("Untitled")}
          </LinkOverlay>
        </Text>
        <Text isTruncated fontSize="xs" color="smMuted">
          {deadline.detail ? `${deadlineLabel(deadline.kind)} · ${deadline.detail}` : deadlineLabel(deadline.kind)}
        </Text>
        {deadline.kind === DeadlineKind.QuestionDeadline && deadline.openCount != null && (
          <UnansweredQuestionsBadge count={deadline.openCount} />
        )}
      </Box>
    </LinkBox>
  );
};
